import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
import collaborationCopy from '@/i18n/ns/workspace-collaboration';
// Meeting canvas dispatcher (BUILD 1, WP-0b).
//
// It picks the view for the meeting's state and owns only what the slots
// share: the media player, `jump` and the raw/repaired toggle. Every view is
// a slot with one owner package (meeting-slots.ts lists them):
//   capturing, processing/sealing   → MeetingCaptureView (WP-4)
//   processing                      → MeetingProcessingView (WP-5)
//   ready, first notes being written → MeetingFinishingView (PL-30), until the
//     notes exist or the person asks for the transcript now
//   failed without a transcript     → MeetingFailedView (WP-5), then what was
//     heard live (MeetingFinishingTranscript, PL-30)
//   ready, or failed with a transcript →
//     MeetingNotesBanner (WP-5), attendees, player, MeetingSpeakersPanel (WP-6),
//     MeetingRepairPanel (WP-8), MeetingNotesPanel (WP-5), MeetingTranscript
//     (WP-6) whose rows render MeetingRepairText (WP-8).
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useCompany } from '@/company/context';
import meetingsNotesCopy from '@/i18n/ns/meetings-notes';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { workspaceApi } from './api';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { announceMeetingUpdated } from './meeting-events';
import { makeMeetingPrivate, meetingShareItem } from './meeting-sharing';
import { audienceLabel } from './sharing-api';
import { ProvenancePanel } from './provenance-panel';
import { MeetingCaptureView } from './meeting-capture-view';
import { MeetingNavigation, type MeetingView } from './meeting-navigation';
import { MeetingMemberNotes, emptyMeetingNoteDraft } from './meeting-member-notes';
import { MeetingNotesBanner, MeetingNotesPanel } from './meeting-notes';
import {
  MeetingFinishingTranscript,
  MeetingFinishingView,
  useFinishingArrival,
} from './meeting-finishing';
import { meetingSettling } from './meeting-finishing-model';
import { MeetingFailedView, MeetingProcessingView } from './meeting-processing';
import { MeetingRepairPanel, MeetingRepairText } from './meeting-repair';
import { MeetingSpeakersPanel } from './meeting-speakers';
import { MeetingTranscript } from './meeting-transcript';
import type { MeetingSegmentText } from './meeting-slots';
import type { CompanyMeeting } from './types';

const MEETING_AUDIO = (mimeType: string) => mimeType.startsWith('audio/');

function toldAfterMakePrivate(
  meeting: CompanyMeeting,
  notified: string[],
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  const adder = meeting.createdBy ?? null;
  const owner = meeting.fileCreatedBy ?? adder;
  const adderTold = Boolean(adder && notified.includes(adder));
  const ownerTold = Boolean(owner && owner !== adder && notified.includes(owner));
  if (adderTold && ownerTold)
    return uiText(
      'Made private. The person who added it and the owner of its recording have been told in their Inbox.',
    );
  if (adderTold)
    return uiText('Made private. The person who added it has been told in their Inbox.');
  if (ownerTold)
    return uiText('Made private. The owner of its recording has been told in their Inbox.');
  return uiText('Made private. Its creator could not be notified.');
}

function MeetingPrivacy({
  meeting,
  onChanged,
}: {
  meeting: CompanyMeeting;
  onChanged: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const company = useCompany();
  const [busy, setBusy] = useState(false);
  const admin = company.data?.membership?.permissions?.makeCompanyWorkPrivate === true;
  if (!meeting.visibility || meeting.visibility === 'private' || (!meeting.canShare && !admin))
    return null;
  async function makePrivate(uiText: WorkspaceText = workspaceTextFor('en')) {
    setBusy(true);
    try {
      const outcome = await makeMeetingPrivate(meeting);
      if (!meeting.canShare) {
        ws.openCanvas(null);
        announceMeetingUpdated();
        ws.notify(toldAfterMakePrivate(meeting, outcome.notifiedUserIds ?? [], uiText));
      } else {
        ws.notify(uiText('Made private. Only you can see this meeting.'));
        onChanged();
      }
    } catch (failure) {
      ws.notify(
        failure instanceof Error ? failure.message : uiText('Could not make this meeting private.'),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <button className="ws-button" disabled={busy} onClick={() => void makePrivate(uiText)}>
      <WorkspaceIcon name="lock" size={14} /> {busy ? uiText('Saving…') : uiText('Make private')}
    </button>
  );
}

function MeetingHeading({
  meeting,
  onChanged,
}: {
  meeting: CompanyMeeting;
  onChanged: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const collaboration = useNamespaceCopy(collaborationCopy);
  const ws = useWorkspace();
  const creator =
    meeting.createdBy && meeting.createdBy !== ws.user?.id ? meeting.createdByName : null;
  return (
    <>
      <div className="ws-eyebrow">
        {uiRich('Meeting · {value1} {value2} · Original retained', {
          value1: audienceLabel(meetingShareItem(meeting), ws.companyName, uiText.locale),
          value2: creator ? ` · ${formatCopy(collaboration.addedBy, { name: creator })}` : '',
        })}
      </div>
      <div className="ws-meeting-heading">
        {/* Focusable for the arrival from the finishing view (PL-30). */}
        <h1 tabIndex={-1}>{meeting.title}</h1>
        <MeetingPrivacy meeting={meeting} onChanged={onChanged} />
      </div>
    </>
  );
}

/** The recorder's views: capturing, and processing while the server seals the recording. */
const isCaptureState = (meeting: CompanyMeeting) =>
  meeting.status === 'capturing' ||
  (meeting.status === 'processing' && meeting.stage === 'sealing');

/** Voice-pass states that are about to change (BUILD 2 C2-10). */
const VOICE_PASS_PENDING: ReadonlySet<string> = new Set(['queued', 'running', 'stale']);

/**
 * Whether the open canvas should keep re-reading the meeting: while it is
 * recorded, sealed or processed, and while a ready meeting's notes,
 * transcript repair or voice pass are being regenerated.
 */
export function meetingNeedsRefresh(meeting: CompanyMeeting | null | undefined): boolean {
  if (!meeting) return false;
  if (meeting.status === 'capturing' || meeting.status === 'processing') return true;
  if (meeting.status !== 'ready') return false;
  const notes = meeting.notes?.status;
  const repair = meeting.repair?.status;
  return (
    notes === 'generating' ||
    notes === 'stale' ||
    repair === 'pending' ||
    repair === 'running' ||
    repair === 'stale' ||
    VOICE_PASS_PENDING.has(meeting.voice?.status ?? '')
  );
}

/**
 * Whether a just-finished meeting still shows the finishing view while its
 * first notes are written (PL-30). Once this canvas has shown the finished
 * view, or the person chose "View transcript now", it never goes back.
 */
function useSettling(meeting: CompanyMeeting) {
  const [finished, setFinished] = useState<string | null>(null);
  const active = finished !== meeting.id && meetingSettling(meeting, Date.now());
  const shown = !active && meeting.status === 'ready';
  useEffect(() => {
    if (shown) setFinished(meeting.id);
  }, [shown, meeting.id]);
  return { active, showNow: () => setFinished(meeting.id) };
}

function MeetingCanvasBody({
  meeting,
  onRetry,
  initialView = 'canvas',
}: {
  meeting: CompanyMeeting;
  /** Reload the meeting detail. */
  onRetry: () => void;
  initialView?: MeetingView;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const copy = useNamespaceCopy(meetingsNotesCopy);
  const [view, setView] = useState<MeetingView>(initialView);
  const navigation = <MeetingNavigation id={meeting.id} view={view} onChange={setView} />;
  const showTranscript = view === 'transcript';
  const [noteDraft, setNoteDraft] = useState(emptyMeetingNoteDraft);
  useEffect(() => {
    ws.setCanvasDirty(Boolean(noteDraft.content));
    return () => ws.setCanvasDirty(false);
  }, [noteDraft.content, ws.setCanvasDirty]);
  const memberNotes = !showTranscript ? (
    <MeetingMemberNotes meeting={meeting} draft={noteDraft} onDraft={setNoteDraft} />
  ) : null;
  const panel = {
    id: `meeting-${meeting.id}-panel`,
    role: 'tabpanel',
    tabIndex: 0,
    'aria-labelledby': `meeting-${meeting.id}-${view}-tab`,
  };
  const player = useRef<HTMLMediaElement>(null);
  const [raw, setRaw] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const notify = ws.notify;
  const mimeType = meeting.file?.mimeType ?? '';
  const media = mimeType === 'video/mp4' || MEETING_AUDIO(mimeType);
  const loadMediaUrl = useCallback(() => {
    if (!media || !meeting.fileId) return;
    void workspaceApi
      .fileDownloadTarget(meeting.fileId, true)
      .then((target) => setMediaUrl(target.url ?? ''))
      .catch((failure) =>
        notify(
          failure instanceof Error ? failure.message : uiText('The recording could not be opened.'),
        ),
      );
  }, [media, meeting.fileId, notify]);
  useEffect(loadMediaUrl, [loadMediaUrl]);
  const jump = (index: number) => {
    const segment = meeting.segments?.find((item) => item.index === index);
    setView('transcript');
    window.requestAnimationFrame(() =>
      document.getElementById(`meeting-segment-${index}`)?.scrollIntoView({ behavior: 'smooth' }),
    );
    if (player.current && segment?.startMs !== null && segment?.startMs !== undefined)
      player.current.currentTime = segment.startMs / 1000;
  };
  const renderText: MeetingSegmentText = (segment, rawText) => (
    <MeetingRepairText meeting={meeting} segment={segment} raw={rawText} onChanged={onRetry} />
  );
  const settling = useSettling(meeting);
  const finishing = isCaptureState(meeting) || meeting.status === 'processing' || settling.active;
  const body = useRef<HTMLDivElement>(null);
  useFinishingArrival(meeting, finishing, body);
  if (isCaptureState(meeting))
    return (
      <MeetingCaptureView
        meeting={meeting}
        onChanged={onRetry}
        memberNotes={memberNotes}
        navigation={navigation}
        showTranscript={showTranscript}
        panel={panel}
      />
    );
  if (meeting.status === 'processing')
    return (
      <MeetingProcessingView
        meeting={meeting}
        memberNotes={memberNotes}
        navigation={navigation}
        showTranscript={showTranscript}
        panel={panel}
      />
    );
  if (settling.active)
    return (
      <MeetingFinishingView
        meeting={meeting}
        onShowTranscript={() => {
          setView('transcript');
          settling.showNow();
        }}
        memberNotes={memberNotes}
        navigation={navigation}
        showTranscript={showTranscript}
        panel={panel}
      />
    );
  if (meeting.status === 'failed' && !meeting.segments?.length)
    return (
      <>
        <MeetingFailedView
          meeting={meeting}
          onRetry={onRetry}
          memberNotes={memberNotes}
          navigation={navigation}
          showTranscript={showTranscript}
          panel={panel}
        />
        {showTranscript && meeting.captureMethod === 'browser' && (
          <MeetingFinishingTranscript meeting={meeting} />
        )}
      </>
    );
  return (
    <div className="ws-canvas-body ws-meeting-canvas-body" ref={body}>
      <MeetingHeading meeting={meeting} onChanged={onRetry} />
      {navigation}
      <div {...panel}>
        <MeetingNotesBanner meeting={meeting} onRetry={onRetry} />
        <p className="ws-muted">
          {meeting.participants?.map((item) => item.displayName).join(', ') || copy.noAttendees}
        </p>
        {mimeType === 'video/mp4' ? (
          <video
            ref={player as RefObject<HTMLVideoElement>}
            className="ws-meeting-player"
            controls
            preload="metadata"
            src={mediaUrl}
            onError={loadMediaUrl}
          />
        ) : MEETING_AUDIO(mimeType) ? (
          <audio
            ref={player as RefObject<HTMLAudioElement>}
            className="ws-meeting-player"
            controls
            preload="metadata"
            src={mediaUrl}
            onError={loadMediaUrl}
          />
        ) : null}
        {showTranscript && (
          <MeetingSpeakersPanel meeting={meeting} player={player} onChanged={onRetry} />
        )}
        {showTranscript && <MeetingRepairPanel meeting={meeting} jump={jump} onChanged={onRetry} />}
        {memberNotes}
        {!showTranscript && <MeetingNotesPanel meeting={meeting} jump={jump} />}
        {showTranscript && (
          <MeetingTranscript
            meeting={meeting}
            player={player}
            raw={raw}
            onRawChange={setRaw}
            renderText={renderText}
          />
        )}
        <ProvenancePanel kind="meeting" id={meeting.id} />
      </div>
    </div>
  );
}

export function MeetingCanvas(props: {
  meeting: CompanyMeeting;
  onRetry: () => void;
  initialView?: MeetingView;
}) {
  const ws = useWorkspace();
  return <MeetingCanvasBody key={`${ws.user?.id}:${props.meeting.id}`} {...props} />;
}
